import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError } from '@/services/api';
import * as authService from '@/services/auth';
import type { AuthUser } from '@/services/auth';

type AuthState =
  | { status: 'loading'; user: null }
  | { status: 'authenticated'; user: AuthUser }
  | { status: 'anonymous'; user: null };

interface AuthContextValue {
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading', user: null });

  const refreshFromMe = useCallback(async () => {
    try {
      const user = await authService.fetchMe();
      setState({ status: 'authenticated', user });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        try {
          await authService.refresh();
          const user = await authService.fetchMe();
          setState({ status: 'authenticated', user });
          return;
        } catch {
          // refresh falló también; quedamos como anonymous
        }
      }
      setState({ status: 'anonymous', user: null });
    }
  }, []);

  useEffect(() => {
    void refreshFromMe();
  }, [refreshFromMe]);

  const login = useCallback(async (email: string, password: string) => {
    const user = await authService.login(email, password);
    setState({ status: 'authenticated', user });
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      setState({ status: 'anonymous', user: null });
    }
  }, []);

  const value = useMemo(
    () => ({ state, login, logout, refreshUser: refreshFromMe }),
    [state, login, logout, refreshFromMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
